import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api } from '../utils/api';
import {
  registerCrypto,
  loginCrypto,
  unlockCrypto,
} from '../utils/vault-crypto';
import { zeroMemory } from '@barmetvault/crypto';
import { startAuthentication } from '@simplewebauthn/browser';

/* ── Types ────────────────────────────────────────── */

/** BHM-CLD-REQ-034: email original con el que se derivó el hash de autenticación. */
export function authSaltEmailOf(kdfParams: unknown): string | undefined {
  try {
    const p = typeof kdfParams === 'string' ? JSON.parse(kdfParams) : kdfParams;
    const v = p && typeof p === 'object' ? (p as Record<string, unknown>).authSaltEmail : undefined;
    return typeof v === 'string' && v.includes('@') ? v.toLowerCase().trim() : undefined;
  } catch {
    return undefined;
  }
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  name?: string;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
  avatarUrl?: string;
  mfaEnabled: boolean;
  createdAt: string;
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

interface PendingMfa {
  tempToken: string;
  masterKey: Uint8Array;
  kdfSalt: string;
}

interface AuthState {
  user: User | null;
  tokens: Tokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLocked: boolean;
  error: string | null;
  pendingMfa: PendingMfa | null;

  // Crypto state — NEVER persisted, only in Zustand memory
  /** The master key derived from the user's password. Cleared on lock/logout. */
  masterKey: Uint8Array | null;
  /** The user's KDF salt (base64), stored for re-derivation on unlock. */
  kdfSalt: string | null;
  /** Encrypted private keys blob from server (base64). */
  encryptedPrivateKeys: string | null;
  privateKeyNonce: string | null;
  kekSalt: string | null;

  // Actions
  register: (email: string, masterPassword: string, invitationToken: string) => Promise<void>;
  login: (email: string, masterPassword: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  loginWithPasskey: (email: string) => Promise<void>;
  unlock: (masterPassword: string) => Promise<void>;
  lock: () => void;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  updateProfile: (displayName: string) => Promise<void>;
  clearError: () => void;
}

/* ── Store ────────────────────────────────────────── */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      isLocked: false,
      error: null,
      pendingMfa: null,

      // Crypto state — memory only, never persisted
      masterKey: null,
      kdfSalt: null,
      encryptedPrivateKeys: null,
      privateKeyNonce: null,
      kekSalt: null,

      /**
       * Register a new user.
       * Performs all client-side crypto: derives master key, generates keypairs,
       * encrypts private keys. The master password NEVER leaves the client.
       */
      register: async (
        email: string,
        masterPassword: string,
        invitationToken: string,
      ) => {
        set({ isLoading: true, error: null });
        let masterKey: Uint8Array | null = null;
        try {
          // Perform client-side crypto
          const cryptoResult = await registerCrypto(email, masterPassword);
          const { payload } = cryptoResult;
          masterKey = cryptoResult.masterKey;

          // Send encrypted registration data to server
          const { data } = await api.post('/auth/register', {
            email: email.toLowerCase().trim(),
            invitationToken,
            ...payload,
          });

          // Cache master key in memory (NEVER persisted)
          set({
            user: data.user,
            tokens: {
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              expiresAt: Date.now() + data.expiresIn * 1000,
            },
            isAuthenticated: true,
            isLocked: false,
            isLoading: false,
            masterKey,
            kdfSalt: payload.kdfSalt,
            encryptedPrivateKeys: null, // First registration, keys are fresh
            privateKeyNonce: null,
            kekSalt: payload.kekSalt,
          });
        } catch (err: unknown) {
          // Do not retain a derived master key if registration did not finish.
          if (masterKey) zeroMemory(masterKey);
          const message =
            err instanceof Error ? err.message : 'Registration failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      /**
       * Login with email and master password.
       * The master password NEVER leaves the client.
       */
      login: async (email: string, masterPassword: string) => {
        set({ isLoading: true, error: null });
        let masterKey: Uint8Array | null = null;
        try {
          const orgSlug = import.meta.env.VITE_ORG_SLUG ?? 'barmetmedia';
          // Step 1: Get the user's KDF salt from the server
          const { data: saltData } = await api.post('/auth/salt', {
            email: email.toLowerCase().trim(),
            orgSlug,
          });

          // Step 2: Derive auth hash + master key client-side.
          // BHM-CLD-REQ-034: si la cuenta cambió de email sin cambiar contraseña,
          // el hash de autenticación sigue derivándose con el email original
          // (kdfParams.authSaltEmail). La clave maestra no depende del email.
          const cryptoResult = await loginCrypto(
            authSaltEmailOf(saltData.kdfParams) ?? email,
            masterPassword,
            saltData.kdfSalt,
          );
          const { authHash } = cryptoResult;
          masterKey = cryptoResult.masterKey;

          // Step 3: Send auth hash to server (NOT the password)
          const { data } = await api.post('/auth/login', {
            email: email.toLowerCase().trim(),
            authHash,
            orgSlug,
          });

          if (data.mfaRequired) {
            set({
              pendingMfa: { tempToken: data.tempToken, masterKey, kdfSalt: saltData.kdfSalt },
              isLoading: false,
            });
            return;
          }

          // Step 4: Cache master key in memory only
          set({
            user: data.user,
            tokens: {
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              expiresAt: Date.now() + data.expiresIn * 1000,
            },
            isAuthenticated: true,
            isLocked: false,
            isLoading: false,
            masterKey,
            kdfSalt: saltData.kdfSalt,
            encryptedPrivateKeys: data.encryptedPrivateKey || null,
            privateKeyNonce: data.privateKeyNonce || null,
            kekSalt: data.kekSalt || null,
          });
        } catch (err: unknown) {
          // A failed API login must not leave a key derived from the password
          // alive in the page process.
          if (masterKey) zeroMemory(masterKey);
          const message =
            err instanceof Error ? err.message : 'Authentication failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      verifyMfa: async (code) => {
        const pendingMfa = get().pendingMfa;
        if (!pendingMfa) throw new Error('No hay una verificación MFA pendiente.');
        const { data } = await api.post('/auth/mfa/verify', {
          code: code.replace(/\s/g, ''),
          tempToken: pendingMfa.tempToken,
        });
        set({
          user: data.user,
          tokens: {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            expiresAt: Date.now() + data.expiresIn * 1000,
          },
          isAuthenticated: true,
          isLocked: false,
          masterKey: pendingMfa.masterKey,
          kdfSalt: pendingMfa.kdfSalt,
          encryptedPrivateKeys: data.encryptedPrivateKey || null,
          privateKeyNonce: data.privateKeyNonce || null,
          kekSalt: data.kekSalt || null,
          pendingMfa: null,
        });
      },

      loginWithPasskey: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          const normalizedEmail = email.toLowerCase().trim();
          if (!normalizedEmail) throw new Error('Email is required');
          const orgSlug = import.meta.env.VITE_ORG_SLUG ?? 'barmetmedia';

          const [{ data: beginData }, { data: saltData }] = await Promise.all([
            api.post('/auth/webauthn/login/begin', {
              email: normalizedEmail,
              orgSlug,
            }),
            api.post('/auth/salt', { email: normalizedEmail, orgSlug }),
          ]);
          const credential = await startAuthentication({
            optionsJSON: beginData.options,
          });
          const { data } = await api.post('/auth/webauthn/login/finish', {
            userId: beginData.userId,
            response: credential,
          });
          set({
            user: data.user,
            tokens: {
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              expiresAt: Date.now() + data.expiresIn * 1000,
            },
            isAuthenticated: true,
            isLoading: false,
            // Note: passkey login does NOT give us the master key.
            // User must enter password to unlock vault operations.
            isLocked: true,
            kdfSalt: saltData.kdfSalt,
            encryptedPrivateKeys: data.encryptedPrivateKey || null,
            privateKeyNonce: data.privateKeyNonce || null,
            kekSalt: data.kekSalt || null,
          });
        } catch (err: unknown) {
          const message =
            err instanceof Error ? err.message : 'Passkey authentication failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      /**
       * Unlock the vault by re-deriving the master key from the password.
       * Called after idle timeout or passkey login.
       */
      unlock: async (masterPassword: string) => {
        const { kdfSalt } = get();
        if (!kdfSalt) {
          set({ error: 'No KDF salt available — please log in again' });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const masterKey = await unlockCrypto(masterPassword, kdfSalt);
          set({
            masterKey,
            isLocked: false,
            isLoading: false,
          });
        } catch (err: unknown) {
          const message =
            err instanceof Error ? err.message : 'Unlock failed';
          set({ error: message, isLoading: false });
          throw err;
        }
      },

      /**
       * Lock the vault — clears the master key from memory.
       * User remains authenticated but cannot access vault items.
       */
      lock: () => {
        const { masterKey } = get();
        if (masterKey) zeroMemory(masterKey);
        set({
          masterKey: null,
          isLocked: true,
        });
      },

      logout: () => {
        const { masterKey } = get();
        if (masterKey) zeroMemory(masterKey);

        const refreshToken = get().tokens?.refreshToken;
        if (refreshToken) {
          api.post('/auth/logout', { refreshToken }).catch(() => {});
        }
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLocked: false,
          error: null,
          pendingMfa: null,
          masterKey: null,
          kdfSalt: null,
          encryptedPrivateKeys: null,
          privateKeyNonce: null,
          kekSalt: null,
        });
      },

      refreshAuth: async () => {
        const refreshToken = get().tokens?.refreshToken;
        if (!refreshToken) {
          get().logout();
          return;
        }
        try {
          const { data } = await api.post('/auth/refresh', { refreshToken });
          set({
            tokens: {
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              expiresAt: Date.now() + data.expiresIn * 1000,
            },
          });
        } catch {
          get().logout();
        }
      },

      updateProfile: async (displayName: string) => {
        const { data } = await api.patch('/auth/profile', { displayName });
        set({ user: data });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'barmetvault-auth',
      storage: createJSONStorage(() => sessionStorage),
      /**
       * Only persist non-sensitive state. The masterKey and kdfSalt
       * are intentionally excluded — they live in memory only.
       */
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        kdfSalt: state.kdfSalt,
        encryptedPrivateKeys: state.encryptedPrivateKeys,
        privateKeyNonce: state.privateKeyNonce,
        kekSalt: state.kekSalt,
      }),
      onRehydrateStorage: () => (state) => {
        // The master key is intentionally never persisted.  A restored session
        // must therefore be locked until the user derives the key again.
        if (state?.isAuthenticated && !state.masterKey) state.lock();
      },
    },
  ),
);
